"use client";

import * as React from "react";
import { Clock, Mail, MapPin, Phone, ShieldCheck, Snowflake } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, type MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
function RecursiveFooterMenuLinks({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  return <ul className={depth === 0 ? "space-y-2.5 min-w-0" : "pl-3 mt-1.5 space-y-1.5 border-l border-border min-w-0"}>
      {items.map((item, i) => <li key={item.id} data-index={i} className="min-w-0">
          <Link to={item.href || "#"} className="text-sm text-muted-foreground transition-colors hover:text-foreground break-words">
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <RecursiveFooterMenuLinks items={item.children} depth={depth + 1} />}
        </li>)}
    </ul>;
}
function FooterNavigationConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-2.5">
        <div className="h-4 w-28 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-36 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-24 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    const fallbackLinks = [{
      id: "1",
      label: "Refrigerated Trailers",
      href: "/RefrigeratedTrailersPage"
    }, {
      id: "2",
      label: "Rental Options & Pricing",
      href: "/RentalOptionsPricingPage"
    }, {
      id: "3",
      label: "Who We Serve",
      href: "/WhoWeServePage"
    }, {
      id: "4",
      label: "Service Areas",
      href: "/ServiceAreasPage"
    }, {
      id: "5",
      label: "About Us",
      href: "/AboutUsPage"
    }, {
      id: "6",
      label: "Contact & Request a Quote",
      href: "/ContactRequestAQuotePage"
    }];
    return <RecursiveFooterMenuLinks items={fallbackLinks} />;
  }
  return <RecursiveFooterMenuLinks items={menuItems} />;
}
export default function Footer() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="footer" data-nav="light" className="bg-background text-foreground border-t border-border pt-16 pb-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-8 pb-12">
          {/* Column 1: Brand & Description */}
          <div className="space-y-4 min-w-0">
            <Link to="/" className="inline-block focus-visible:outline-ring">
              <WvcLogo className="h-28 w-auto" />
            </Link>
            <p className="text-sm leading-relaxed text-muted-foreground break-words">Commercial Mobile Refrigerated and Freezer trailer rentals serving Southern California and our listed Las Vegas-area communities.</p>
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-foreground max-w-full">
              <Snowflake className="h-3.5 w-3.5 text-primary shrink-0" />
              <span className="truncate">Mobile Refrigeration Rentals</span>
            </div>
          </div>

          {/* Column 2: Navigation Links */}
          <div className="space-y-4 min-w-0">
            <h3 className="text-sm font-semibold tracking-wider text-foreground uppercase">
              Quick Links
            </h3>
            <MenuProvider menu_id="17" className="w-full min-w-0">
              <FooterNavigationConsumer />
            </MenuProvider>
          </div>

          {/* Column 3: Contact & Direct Dispatch */}
          <div className="space-y-4 min-w-0">
            <h3 className="text-sm font-semibold tracking-wider text-foreground uppercase">
              Contact
            </h3>
            <ul className="space-y-3 text-sm min-w-0">
              <li className="flex items-center gap-2.5 text-muted-foreground min-w-0">
                <Phone className="h-4 w-4 text-primary shrink-0" />
                <a href="tel:2135218821" className="transition-colors hover:text-foreground break-words min-w-0">
                  213-521-8821
                </a>
              </li>
              <li className="flex items-center gap-2.5 text-muted-foreground min-w-0">
                <Mail className="h-4 w-4 text-primary shrink-0" />
                <a href="mailto:Info@Fridgefleet.com" className="transition-colors hover:text-foreground break-all sm:break-words min-w-0">
                  Info@Fridgefleet.com
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-muted-foreground min-w-0">
                <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span className="break-words min-w-0">Southern California & Las Vegas Area</span>
              </li>
            </ul>
            <div className="pt-2">
              <Button asChild size="sm" className="w-full sm:w-auto">
                <Link to="/ContactRequestAQuotePage">Request a Quote</Link>
              </Button>
            </div>
          </div>

          {/* Column 4: Operational Hours & Compliance */}
          <div className="space-y-4 min-w-0">
            <h3 className="text-sm font-semibold tracking-wider text-foreground uppercase">
              Business Hours
            </h3>
            <div className="space-y-2 text-sm text-muted-foreground min-w-0">
              <div className="flex items-start gap-2 min-w-0">
                <Clock className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span className="break-words min-w-0">Open 7 days a week, 7:00 a.m.–6:00 p.m.</span>
              </div>
              <div className="flex items-start gap-2 pt-1 min-w-0">
                <ShieldCheck className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <Link to="/RefrigeratedTrailersPage" className="transition-colors hover:text-foreground break-words min-w-0">
                  View trailer options and available specifications.
                </Link>
              </div>
            </div>
            <div className="pt-2 flex flex-col gap-1.5 min-w-0">
              <Link to="/PrivacyPolicyTermsPage" className="text-xs text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground break-words">
                Privacy Policy
              </Link>
              <Link to="/PrivacyPolicyTermsPage" className="text-xs text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground break-words">
                Terms and Conditions
              </Link>
            </div>
          </div>
        </motion.div>

        <Separator className="mb-8" />

        {/* Sub-footer */}
        <div className="flex flex-col items-center justify-between gap-4 text-center md:flex-row md:text-left text-xs text-muted-foreground min-w-0">
          <p className="break-words min-w-0">© {new Date().getFullYear()} Fridge Fleet LLC. All rights reserved.</p>
          <p className="max-w-md break-words min-w-0">
            Commercial cold storage trailer rentals. Specs, availability, and scheduling subject to final rental agreement.
          </p>
        </div>
      </div>
    </section>;
}